import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "motion/react";
import { ArrowUpLeft, Play, Pause } from "lucide-react";

/* ─── bilingual content ──────────────────────────────── */

const CONTENT = {
  en: {
    back: "Portfolio",
    meta: "experimental imagery",
    heroSub: "Designer · Image-maker",
    heroLocation: "Vancouver, BC",
    sections: {
      fragments: "fragments",
      note: "note",
      still: "still",
      onBeing: "on being",
    },
    addImage: "add image",
    fragmentLabels: ["untitled, 2023", "untitled, 2024", "untitled, 2023", "untitled, 2024"],
    largeLabel: "location, year",
    pairLabels: ["untitled, 2024", "untitled, 2023"],
    poem: [
      "I make things feel human for a living.",
      "On weekends, I look for the light",
      "in rooms that don't know they're beautiful.",
      " ",
      "I grew up between two languages.",
      "I design in the space between them.",
    ],
    about:
      "There's a kind of attention I bring to software design that I also bring to a quiet room, a need to understand what something is asking of the people inside it. Psychology taught me that spaces shape behaviour before behaviour shapes spaces. I haven't stopped thinking about that.",
    closeName: "YOUNG HE",
    closeLocation: "Vancouver, BC",
  },
  zh: {
    back: "作品集",
    meta: "实验影像",
    heroSub: "设计师 · 影像创作者",
    heroLocation: "温哥华，不列颠哥伦比亚",
    sections: {
      fragments: "碎片",
      note: "小记",
      still: "静帧",
      onBeing: "关于存在",
    },
    addImage: "添加图片",
    fragmentLabels: ["无题，2023", "无题，2024", "无题，2023", "无题，2024"],
    largeLabel: "地点，年份",
    pairLabels: ["无题，2024", "无题，2023"],
    poem: [
      "以让事物变得有人情味为生。",
      "周末，寻找光. ",
      "落在不知道自己美丽的房间里。",
      " ",
      "在两种语言之间长大。",
      "在它们的间隙里，设计。",
    ],
    about:
      "面对软件设计时，我有一种专注. 进入安静的房间时，也是同样的专注。一种渴望：理解这个空间，对身处其中的人，究竟提出了怎样的要求。心理学告诉我，空间塑造行为，先于行为塑造空间。我一直在想这件事。",
    closeName: "何 杨",
    closeLocation: "温哥华，不列颠哥伦比亚",
  },
};

const FRAGMENT_SHAPES = [
  { aspect: "aspect-[3/4]",  rotate: "-1deg",   delay: 0.0 },
  { aspect: "aspect-[4/5]",  rotate: "0.8deg",  delay: 0.1 },
  { aspect: "aspect-[1/1]",  rotate: "-0.5deg", delay: 0.2 },
  { aspect: "aspect-[3/4]",  rotate: "1.2deg",  delay: 0.3 },
];

/* ─── grain overlay ──────────────────────────────────── */

const GRAIN_URI =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.72' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)'/%3E%3C/svg%3E";

const Grain = () => (
  <div
    aria-hidden="true"
    className="pointer-events-none fixed inset-0 z-[200]"
    style={{ backgroundImage: `url("${GRAIN_URI}")`, backgroundRepeat: "repeat", opacity: 0.055 }}
  />
);

/* ─── scroll reveal ──────────────────────────────────── */

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 1.1, delay, ease: [0.25, 0.1, 0.25, 1] }}
  >
    {children}
  </motion.div>
);

/* ─── image slot ─────────────────────────────────────── */

const ImageSlot = ({ label, aspect, rotate, delay, addImageLabel }) => (
  <Reveal delay={delay}>
    <div
      className="group relative cursor-pointer"
      style={{ transform: `rotate(${rotate})`, transformOrigin: "center" }}
    >
      <div className={`w-full ${aspect} bg-[#E0DDD6] overflow-hidden`}>
        {/* replace this div with <img> or <video> */}
        <div className="w-full h-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <span className="text-[#9A9790] text-xs tracking-widest uppercase">{addImageLabel}</span>
        </div>
      </div>
      <p className="mt-2 text-[10px] tracking-widest text-[#9A9790] uppercase">
        [ {label} ]
      </p>
    </div>
  </Reveal>
);

/* ─── language toggle ────────────────────────────────── */

const LangToggle = ({ lang, setLang }) => (
  <button
    onClick={() => setLang(lang === "en" ? "zh" : "en")}
    aria-label="Switch language"
    className="fixed top-6 right-6 z-[150] flex items-center gap-1.5 transition-colors duration-300"
    style={{ textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}
  >
    <span
      className="text-xs tracking-widest transition-all duration-300"
      style={{ color: lang === "en" ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.4)" }}
    >
      EN
    </span>
    <span className="text-xs text-white/30">/</span>
    <span
      className="text-xs tracking-widest transition-all duration-300"
      style={{ color: lang === "zh" ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.4)" }}
    >
      中文
    </span>
  </button>
);

/* ─── music player ──────────────────────────────────── */

const BARS = [
  { delay: 0.00, dur: 0.65 },
  { delay: 0.18, dur: 0.50 },
  { delay: 0.07, dur: 0.72 },
  { delay: 0.28, dur: 0.58 },
  { delay: 0.12, dur: 0.62 },
];

const WaveBar = ({ delay, dur, playing }) => (
  <motion.span
    className="block w-[2px] rounded-full origin-bottom"
    style={{ height: 14, background: "rgba(255,255,255,0.45)" }}
    animate={playing
      ? { scaleY: [0.15, 1, 0.35, 0.85, 0.15] }
      : { scaleY: 0.15 }
    }
    transition={{
      duration: dur,
      delay,
      repeat: Infinity,
      ease: "easeInOut",
    }}
  />
);

const MusicPlayer = () => {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      audio.play().catch(() => {});
    }
    setPlaying(p => !p);
  };

  return (
    <div className="fixed bottom-7 left-1/2 -translate-x-1/2 z-[150]">
      {/* swap /music.mp3 for your track once you have it */}
      <audio ref={audioRef} src="/music.mp3" loop preload="none" />

      <motion.div
        className="flex items-center gap-4 pl-4 pr-3 py-2.5 rounded-full select-none"
        style={{
          background: "rgba(8,8,8,0.72)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          border: "1px solid rgba(255,255,255,0.07)",
          boxShadow: "0 4px 32px rgba(0,0,0,0.35)",
        }}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.1, delay: 3.0, ease: [0.25, 0.1, 0.25, 1] }}
      >
        {/* animated waveform */}
        <div className="flex items-center gap-[3px]" style={{ height: 14 }}>
          {BARS.map((b, i) => (
            <WaveBar key={i} delay={b.delay} dur={b.dur} playing={playing} />
          ))}
        </div>

        {/* track label */}
        <span className="text-[10px] tracking-[0.22em] text-white/35 uppercase whitespace-nowrap">
          [ untitled ]
        </span>

        {/* divider */}
        <span className="w-px h-3 bg-white/10 flex-shrink-0" />

        {/* play / pause */}
        <button
          onClick={toggle}
          aria-label={playing ? "Pause" : "Play"}
          className="w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0"
          style={{
            border: "1px solid rgba(255,255,255,0.18)",
            color: playing ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.5)",
            background: playing ? "rgba(255,255,255,0.08)" : "transparent",
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.45)";
            e.currentTarget.style.color = "rgba(255,255,255,0.9)";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)";
            e.currentTarget.style.color = playing
              ? "rgba(255,255,255,0.9)"
              : "rgba(255,255,255,0.5)";
          }}
        >
          {playing
            ? <Pause size={9} strokeWidth={2.5} />
            : <Play  size={9} strokeWidth={2.5} className="translate-x-px" />
          }
        </button>
      </motion.div>
    </div>
  );
};

/* ─── main page ──────────────────────────────────────── */

const Dreamcore = () => {
  const [lang, setLang] = useState("en");
  const t = CONTENT[lang];

  const largeImageRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: largeImageRef,
    offset: ["start end", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <div
      className="min-h-screen relative overflow-x-hidden"
      style={{ background: "#F5F3EE", color: "#1A1918" }}
    >
      <Grain />

      {/* back */}
      <Link
        to="/"
        aria-label="Back to portfolio"
        className="fixed top-6 left-6 z-[150] flex items-center gap-1.5 text-white/70 hover:text-white transition-colors duration-300 group"
        style={{ textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}
      >
        <ArrowUpLeft
          size={14}
          className="group-hover:-translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300"
        />
        <span className="text-xs tracking-widest uppercase">{t.back}</span>
      </Link>

      {/* lang toggle */}
      <LangToggle lang={lang} setLang={setLang} />

      {/* ── 01 hero ───────────────────────────────── */}
      <section className="relative flex flex-col justify-between min-h-screen px-8 sm:px-14 pt-32 pb-14 overflow-hidden">

        {/* video background */}
        <video
          src="/dreamcore_web.mp4"
          autoPlay
          loop
          muted
          playsInline
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* dark overlay */}
        <div className="absolute inset-0 bg-black/50" aria-hidden="true" />

        {/* gradient fade to page background */}
        <div
          className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
          style={{ background: "linear-gradient(to bottom, transparent, #F5F3EE)" }}
          aria-hidden="true"
        />

        {/* content */}
        <motion.p
          className="relative z-10 text-[10px] tracking-[0.3em] text-white/50 uppercase"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.4, delay: 0.3 }}
        >
          {t.meta} &nbsp;/&nbsp; 01
        </motion.p>

        <div className="relative z-10 flex-1 flex flex-col justify-center">
          <motion.h1
            className="font-NeuePower-Regular leading-none tracking-tight text-white"
            style={{ fontSize: "clamp(4rem, 14vw, 13rem)" }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, delay: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          >
            YOUNG
          </motion.h1>
          <motion.h1
            className="font-NeuePower-Regular leading-none tracking-tight"
            style={{
              fontSize: "clamp(4rem, 14vw, 13rem)",
              color: "transparent",
              WebkitTextStroke: "1.5px rgba(255,255,255,0.9)",
            }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 0.4, y: 0 }}
            transition={{ duration: 1.4, delay: 0.65, ease: [0.25, 0.1, 0.25, 1] }}
          >
            HE
          </motion.h1>
        </div>

        <motion.div
          className="relative z-10 flex justify-between items-end"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.2 }}
        >
          <p className="text-[11px] tracking-[0.25em] text-white/50 uppercase max-w-[22ch] leading-relaxed">
            {t.heroSub}<br />{t.heroLocation}
          </p>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            className="text-white/30 text-xs"
          >
            ↓
          </motion.div>
        </motion.div>
      </section>

      {/* ── 02 fragments ──────────────────────────── */}
      <section className="px-8 sm:px-14 py-24">
        <Reveal>
          <p className="text-[10px] tracking-[0.3em] text-[#9A9790] uppercase mb-14">
            02 &nbsp;/&nbsp; {t.sections.fragments}
          </p>
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8 items-start">
          {FRAGMENT_SHAPES.map((shape, i) => (
            <div key={i} className={i % 2 === 1 ? "mt-10 sm:mt-16" : ""}>
              <ImageSlot
                {...shape}
                label={t.fragmentLabels[i]}
                addImageLabel={t.addImage}
              />
            </div>
          ))}
        </div>
      </section>

      {/* ── 03 poem ───────────────────────────────── */}
      <section className="px-8 sm:px-14 py-24 max-w-xl">
        <Reveal>
          <p className="text-[10px] tracking-[0.3em] text-[#9A9790] uppercase mb-14">
            03 &nbsp;/&nbsp; {t.sections.note}
          </p>
        </Reveal>
        <div className="space-y-1">
          {t.poem.map((line, i) => (
            <Reveal key={`${lang}-${i}`} delay={i * 0.07}>
              <p
                className="font-serif leading-loose"
                style={{
                  fontSize: "clamp(1.05rem, 2.2vw, 1.35rem)",
                  color: line === " " ? "transparent" : "#2E2C28",
                  fontStyle: "italic",
                  fontFamily: lang === "zh"
                    ? "'Songti SC', 'SimSun', serif"
                    : undefined,
                }}
              >
                {line}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── 04 large still ────────────────────────── */}
      <section ref={largeImageRef} className="px-8 sm:px-14 py-16">
        <Reveal>
          <p className="text-[10px] tracking-[0.3em] text-[#9A9790] uppercase mb-8">
            04 &nbsp;/&nbsp; {t.sections.still}
          </p>
        </Reveal>
        <div className="overflow-hidden w-full" style={{ maxWidth: "82vw" }}>
          <motion.div style={{ y: imageY }} className="w-full aspect-[16/9] bg-[#E0DDD6] relative">
            {/* replace with <img> or <video> */}
            <div className="absolute inset-0 flex items-end p-6">
              <p className="text-[10px] tracking-widest text-[#9A9790] uppercase">
                [ {t.largeLabel} ]
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 05 on being ───────────────────────────── */}
      <section className="px-8 sm:px-14 py-24 max-w-lg">
        <Reveal>
          <p className="text-[10px] tracking-[0.3em] text-[#9A9790] uppercase mb-10">
            05 &nbsp;/&nbsp; {t.sections.onBeing}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <p
            className="font-serif leading-loose text-[#4A4843]"
            style={{
              fontSize: "clamp(0.9rem, 1.8vw, 1.1rem)",
              fontFamily: lang === "zh"
                ? "'Songti SC', 'SimSun', serif"
                : undefined,
              lineHeight: lang === "zh" ? "2.2" : undefined,
            }}
          >
            {t.about}
          </p>
        </Reveal>
      </section>

      {/* ── 06 pair ───────────────────────────────── */}
      <section className="px-8 sm:px-14 py-8">
        <div className="flex gap-6 sm:gap-10 items-start">
          <Reveal delay={0} className="w-1/2 sm:w-2/5">
            <div
              className="w-full aspect-[2/3] bg-[#E0DDD6]"
              style={{ transform: "rotate(0.6deg)" }}
            />
            <p className="mt-2 text-[10px] tracking-widest text-[#9A9790] uppercase">
              [ {t.pairLabels[0]} ]
            </p>
          </Reveal>
          <Reveal delay={0.15} className="w-1/2 sm:w-2/5 mt-20 sm:mt-28">
            <div
              className="w-full aspect-[3/4] bg-[#E0DDD6]"
              style={{ transform: "rotate(-1.2deg)" }}
            />
            <p className="mt-2 text-[10px] tracking-widest text-[#9A9790] uppercase">
              [ {t.pairLabels[1]} ]
            </p>
          </Reveal>
        </div>
      </section>

      <MusicPlayer />

      {/* ── close ─────────────────────────────────── */}
      <section className="flex flex-col items-center justify-center py-40 gap-3">
        <Reveal>
          <p
            className="font-NeuePower-Regular tracking-widest text-[#1A1918]"
            style={{ fontSize: "clamp(1.1rem, 2.5vw, 1.6rem)" }}
          >
            {t.closeName}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-[10px] tracking-[0.35em] text-[#9A9790] uppercase">
            {t.closeLocation} &nbsp;·&nbsp; {new Date().getFullYear()}
          </p>
        </Reveal>
      </section>
    </div>
  );
};

export default Dreamcore;
